import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  TrophyIcon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Navbar from "../../components/navBar";

const API_URL = process.env.REACT_APP_API_URL;

function getList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

const Highlights = () => {
  const [pastMatches, setPastMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scores, setScores] = useState({});
  const scoresRef = useRef(scores);
  scoresRef.current = scores;
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [players, setPlayers] = useState({});
  const playersRef = useRef(players);
  playersRef.current = players;
  const [matchVideos, setMatchVideos] = useState({});
  const requestedVideosRef = useRef({});
  const [videoLoading, setVideoLoading] = useState({});
  const [failedThumbnails, setFailedThumbnails] = useState({});
  const [playbackErrors, setPlaybackErrors] = useState({});
  const [search, setSearch] = useState("");
  const [selectedCompetition, setSelectedCompetition] = useState("all");
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("desac-sidebar-collapsed") === "true"
  );

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsCollapsed(window.localStorage.getItem("desac-sidebar-collapsed") === "true");
    };
    const updateCollapsedState = (event) => {
      setIsCollapsed(
        typeof event?.detail?.collapsed === "boolean"
          ? event.detail.collapsed
          : window.localStorage.getItem("desac-sidebar-collapsed") === "true"
      );
    };
    window.addEventListener("resize", updateViewport);
    window.addEventListener("storage", updateCollapsedState);
    window.addEventListener("desac-sidebar-toggle", updateCollapsedState);
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("storage", updateCollapsedState);
      window.removeEventListener("desac-sidebar-toggle", updateCollapsedState);
    };
  }, []);

  useEffect(() => {
    if (!selectedMatch) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedMatch(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedMatch]);

  const fetchScores = useCallback(async (matchId) => {
    if (scoresRef.current[matchId]) return scoresRef.current[matchId];
    try {
      const response = await axios.get(`${API_URL}/scores/matches/${matchId}/scores`);
      setScores((previous) => ({ ...previous, [matchId]: response.data }));
      return response.data;
    } catch (scoreError) {
      console.error("Error fetching scores:", scoreError);
      toast.error("Match scores are temporarily unavailable.");
      return [];
    }
  }, []);

  const fetchPlayerName = useCallback(async (playerId) => {
    if (!playerId) return null;
    if (playersRef.current[playerId]) return playersRef.current[playerId];
    try {
      const response = await axios.get(`${API_URL}/players/${playerId}`);
      const playerName = response.data.name;
      setPlayers((previous) => ({ ...previous, [playerId]: playerName }));
      return playerName;
    } catch (playerError) {
      console.error("Error fetching player name:", playerError);
      return "Unknown Player";
    }
  }, []);

  const checkVideosApiEndpoint = useCallback(async () => {
    try {
      await axios.get(`${API_URL}/videos`);
    } catch (videosError) {
      console.error("Videos API endpoint is not available:", videosError);
    }
  }, []);

  const fetchMatchVideo = useCallback(async (matchId) => {
    if (Object.prototype.hasOwnProperty.call(requestedVideosRef.current, matchId)) return;
    requestedVideosRef.current[matchId] = true;
    setVideoLoading((previous) => ({ ...previous, [matchId]: true }));

    try {
      let videoData = [];
      try {
        const response = await axios.get(`${API_URL}/videos/match/${matchId}`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          videoData = response.data;
        } else if (response.data && typeof response.data === "object" && !Array.isArray(response.data)) {
          videoData = [response.data];
        }
      } catch (matchVideoError) {
        console.error("Error fetching match-specific video:", matchVideoError);
      }

      if (videoData.length === 0) {
        const allVideosResponse = await axios.get(`${API_URL}/videos`);
        const allVideos = getList(allVideosResponse.data);
        videoData = allVideos.filter(
          (video) => video.matchId === matchId || video.matchId === Number(matchId)
        );
      }

      setMatchVideos((previous) => ({ ...previous, [matchId]: videoData }));
    } catch (videoError) {
      console.error("Error fetching match videos:", videoError);
      setMatchVideos((previous) => ({ ...previous, [matchId]: [] }));
    } finally {
      setVideoLoading((previous) => ({ ...previous, [matchId]: false }));
    }
  }, []);

  const fetchHighlights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [matchesResponse, teamsResponse, leaguesResponse] = await Promise.all([
        fetch(`${API_URL}/matches`),
        fetch(`${API_URL}/clubs`),
        fetch(`${API_URL}/leagues`),
      ]);
      if (!matchesResponse.ok || !teamsResponse.ok || !leaguesResponse.ok) {
        throw new Error("Match data is temporarily unavailable.");
      }

      const [matches, teams, leagues] = await Promise.all([
        matchesResponse.json(),
        teamsResponse.json(),
        leaguesResponse.json(),
      ]);
      const teamsById = new Map(getList(teams).map((team) => [team.clubId, team]));
      const leaguesById = new Map(getList(leagues).map((league) => [league.leagueId, league]));
      const now = new Date();
      const completedMatches = getList(matches)
        .filter((match) => {
          const date = new Date(match.date);
          return !Number.isNaN(date.getTime()) && date <= now;
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 10)
        .map((match) => ({
          ...match,
          id: match.matchesId,
          leagueName: leaguesById.get(match.leagueId)?.name || "Competition unavailable",
          homeTeam: teamsById.get(match.homeTeamId) || null,
          awayTeam: teamsById.get(match.awayTeamId) || null,
        }));

      setPastMatches(completedMatches);
      completedMatches.forEach((match) => {
        fetchScores(match.matchesId);
        fetchMatchVideo(match.matchesId);
      });
    } catch (fetchError) {
      console.error("Error fetching highlights:", fetchError);
      setError("We couldn't load match highlights. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fetchMatchVideo, fetchScores]);

  const fetchMatchDetails = useCallback(async (match) => {
    setSelectedMatch(match);
    const matchScores = scoresRef.current[match.matchesId] || await fetchScores(match.matchesId);
    await Promise.all(
      (matchScores || []).map((score) => fetchPlayerName(score.playerId))
    );
    await fetchMatchVideo(match.matchesId);
  }, [fetchMatchVideo, fetchPlayerName, fetchScores]);

  useEffect(() => {
    fetchHighlights();
    checkVideosApiEndpoint();
  }, [fetchHighlights, checkVideosApiEndpoint]);

  const competitions = useMemo(
    () => [...new Set(pastMatches.map((match) => match.leagueName).filter(Boolean))],
    [pastMatches]
  );
  const filteredMatches = useMemo(() => {
    const query = search.trim().toLowerCase();
    return pastMatches.filter((match) => {
      const matchesCompetition = selectedCompetition === "all" || match.leagueName === selectedCompetition;
      const videoTitles = (matchVideos[match.matchesId] || []).map((video) => video.title || "").join(" ");
      const searchText = [match.homeTeam?.name, match.awayTeam?.name, match.leagueName, videoTitles]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesCompetition && (!query || searchText.includes(query));
    });
  }, [pastMatches, selectedCompetition, search, matchVideos]);
  const featuredMatch = filteredMatches[0];
  const remainingMatches = filteredMatches.slice(1);
  const sidebarWidth = isDesktop && isCollapsed ? "5rem" : "16rem";
  const hasFilters = Boolean(search) || selectedCompetition !== "all";

  const clearFilters = () => {
    setSearch("");
    setSelectedCompetition("all");
  };

  return (
    <>
      <Navbar />
      <main
        style={{ "--highlights-sidebar-width": sidebarWidth }}
        className="min-h-screen overflow-x-hidden bg-surface-950 pb-12 pt-16 text-white md:ml-64 md:pt-0 lg:ml-[var(--highlights-sidebar-width)]"
      >
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div className="max-w-2xl">
              <p className="eyebrow-accent inline-flex items-center gap-2">
                <VideoCameraIcon aria-hidden="true" className="h-4 w-4" />
                MATCHDAY RECAPS
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Sports highlights</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">
                Revisit recent matches and watch the moments captured in available video highlights.
              </p>
            </div>
            {!loading && !error && (
              <div aria-live="polite" className="inline-flex min-h-10 w-fit items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 text-sm text-gray-300">
                <PlayIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
                {filteredMatches.length} {filteredMatches.length === 1 ? "match" : "matches"}
              </div>
            )}
          </div>
        </header>

        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
          <section aria-label="Search and filter highlights" className="mb-7 grid grid-cols-1 gap-3 rounded-xl border border-line bg-surface-900 p-4 sm:grid-cols-[minmax(0,1fr)_240px_auto] sm:items-center sm:p-5">
            <label className="relative block min-w-0">
              <span className="sr-only">Search teams, competitions or highlights</span>
              <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search teams, competitions or videos"
                className="field min-h-11 pl-10 pr-10 text-sm"
              />
              {search && (
                <button type="button" aria-label="Clear search" onClick={() => setSearch("")} className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-surface-700 hover:text-white">
                  <XMarkIcon aria-hidden="true" className="h-4 w-4" />
                </button>
              )}
            </label>
            <label>
              <span className="sr-only">Filter highlights by competition</span>
              <select value={selectedCompetition} onChange={(event) => setSelectedCompetition(event.target.value)} className="field min-h-11 text-sm">
                <option value="all">All competitions</option>
                {competitions.map((competition) => <option key={competition} value={competition}>{competition}</option>)}
              </select>
            </label>
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="min-h-10 rounded-lg px-3 text-sm font-medium text-gray-300 underline decoration-line-strong underline-offset-4 transition-colors hover:text-white sm:justify-self-end">
                Clear filters
              </button>
            )}
          </section>

          {loading ? (
            <div role="status" aria-label="Loading sports highlights" className="flex min-h-72 items-center justify-center gap-3 rounded-xl border border-line bg-surface-900 text-sm text-gray-300">
              <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-surface-600 border-t-accent" />
              Loading recent matches and highlights…
            </div>
          ) : error ? (
            <section role="alert" className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <VideoCameraIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">Highlights are temporarily unavailable</h2>
              <p className="mt-2 text-sm text-gray-400">{error}</p>
              <button type="button" onClick={fetchHighlights} className="btn-primary mt-5 min-h-11">Try again</button>
            </section>
          ) : filteredMatches.length === 0 ? (
            <section className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <MagnifyingGlassIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">{pastMatches.length ? "No highlights match your search" : "No recent match highlights"}</h2>
              <p className="mt-2 text-sm text-gray-400">
                {pastMatches.length ? "Try a different team, competition or video title." : "Recent completed matches and their available highlights will appear here."}
              </p>
              {hasFilters && <button type="button" onClick={clearFilters} className="btn-secondary mt-5 min-h-11">Clear filters</button>}
            </section>
          ) : (
            <>
              {featuredMatch && (
                <section aria-label="Featured recent match" className="mb-8">
                  <div className="mb-3 flex items-center gap-2">
                    <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent" />
                    <p className="eyebrow-accent">LATEST MATCH</p>
                  </div>
                  <MatchHighlight
                    match={featuredMatch}
                    scores={scores}
                    videos={matchVideos[featuredMatch.matchesId] || []}
                    videoLoading={Boolean(videoLoading[featuredMatch.matchesId])}
                    featured
                    onOpen={fetchMatchDetails}
                    onThumbnailError={(videoId) => setFailedThumbnails((previous) => ({ ...previous, [videoId]: true }))}
                    failedThumbnails={failedThumbnails}
                  />
                </section>
              )}

              {remainingMatches.length > 0 && (
                <section aria-label="More recent matches">
                  <div className="mb-4 flex items-end justify-between gap-3">
                    <div>
                      <p className="eyebrow">RECENT COVERAGE</p>
                      <h2 className="mt-1 text-xl font-semibold tracking-tight text-white">More match highlights</h2>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {remainingMatches.map((match) => (
                      <MatchHighlight
                        key={match.matchesId}
                        match={match}
                        scores={scores}
                        videos={matchVideos[match.matchesId] || []}
                        videoLoading={Boolean(videoLoading[match.matchesId])}
                        onOpen={fetchMatchDetails}
                        onThumbnailError={(videoId) => setFailedThumbnails((previous) => ({ ...previous, [videoId]: true }))}
                        failedThumbnails={failedThumbnails}
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {selectedMatch && (
          <MatchDetailsDialog
            match={selectedMatch}
            scores={scores}
            players={players}
            videos={matchVideos[selectedMatch.matchesId] || []}
            videoLoading={Boolean(videoLoading[selectedMatch.matchesId])}
            playbackErrors={playbackErrors}
            setPlaybackErrors={setPlaybackErrors}
            onClose={() => setSelectedMatch(null)}
          />
        )}
      </main>
    </>
  );
};

function MatchHighlight({ match, scores, videos, videoLoading, featured = false, onOpen, onThumbnailError, failedThumbnails }) {
  const videosWithSource = videos.filter((video) => video.videoUrl);
  const featuredVideo = videosWithSource[0];
  const homeScore = match.homeTeamScore ?? (Array.isArray(scores[match.matchesId])
    ? scores[match.matchesId].filter((score) => score.clubId === match.homeTeamId).length
    : null);
  const awayScore = match.awayTeamScore ?? (Array.isArray(scores[match.matchesId])
    ? scores[match.matchesId].filter((score) => score.clubId === match.awayTeamId).length
    : null);
  const date = new Date(match.date);
  const matchLabel = `${match.homeTeam?.name || "Home team"} vs ${match.awayTeam?.name || "Away team"}`;

  return (
    <article className={`group min-w-0 overflow-hidden rounded-xl border bg-surface-850 transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:bg-surface-800 ${featured ? "border-accent/30" : "border-line"}`}>        <button type="button" onClick={() => onOpen(match)} aria-label={`${videosWithSource.length ? "Watch highlights" : "View match details"}: ${matchLabel}`} className={`relative block w-full overflow-hidden bg-surface-900 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${featured ? "aspect-video sm:aspect-[2.1/1]" : "aspect-video"}`}>
        {featuredVideo?.thumbnailUrl && !failedThumbnails[featuredVideo.id || featuredVideo.videoUrl] ? (
          <img src={featuredVideo.thumbnailUrl} alt={`${matchLabel} highlight thumbnail`} loading="lazy" onError={() => onThumbnailError(featuredVideo.id || featuredVideo.videoUrl)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.025]" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-surface-900 text-gray-500">
            {videoLoading ? <span aria-hidden="true" className="h-6 w-6 animate-spin rounded-full border-2 border-surface-600 border-t-accent" /> : <VideoCameraIcon aria-hidden="true" className="h-8 w-8" />}
            <span className="text-xs">{videoLoading ? "Loading highlights…" : videosWithSource.length ? "Preview unavailable" : "Match recap"}</span>
          </div>
        )}
        <span aria-hidden="true" className="absolute inset-0 bg-black/25 transition-colors group-hover:bg-black/40" />
        <span className="absolute left-3 top-3 inline-flex max-w-[70%] items-center gap-1.5 truncate rounded-md border border-white/10 bg-black/60 px-2.5 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
          <TrophyIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-accent-soft" />
          <span className="truncate">{match.leagueName}</span>
        </span>
        <span aria-hidden="true" className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-accent text-white shadow-lg transition-transform group-hover:scale-105">
          <PlayIcon className="ml-0.5 h-5 w-5" />
        </span>
        <span className="absolute bottom-3 right-3 rounded-md border border-white/10 bg-black/70 px-2 py-1 text-[10px] font-semibold tracking-wider text-white">
          {videosWithSource.length ? `${videosWithSource.length} ${videosWithSource.length === 1 ? "VIDEO" : "VIDEOS"}` : videoLoading ? "LOADING" : "MATCH DETAILS"}
        </span>
      </button>

      <div className={`min-w-0 ${featured ? "p-5 sm:p-7" : "p-4 sm:p-5"}`}>
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <CalendarDaysIcon aria-hidden="true" className="h-4 w-4" />
          <time dateTime={isNaN(date.getTime()) ? undefined : date.toISOString()}>
            {isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </time>
          <span aria-hidden="true">·</span>
          <span>{match.time || ""}</span>
        </div>

        <div className={`grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 rounded-lg border border-line bg-surface-900 ${featured ? "p-4 sm:p-5" : "p-3"}`}>
          <TeamSummary team={match.homeTeam} score={homeScore} align="left" />
          <span className="text-[10px] font-bold tracking-wider text-gray-500">FINAL</span>
          <TeamSummary team={match.awayTeam} score={awayScore} align="right" />
        </div>

        {featuredVideo?.title && <h2 className={`mt-4 line-clamp-2 font-semibold leading-snug text-white ${featured ? "text-xl sm:text-2xl" : "text-base"}`}>{featuredVideo.title}</h2>}
        {featuredVideo?.description && featured && <p className="mt-2 line-clamp-2 text-sm leading-6 text-gray-400">{featuredVideo.description}</p>}
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="min-w-0 truncate text-xs text-gray-500">{match.leagueName}</span>
          <button type="button" onClick={() => onOpen(match)} className="inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-gray-300 transition-colors hover:bg-surface-700 hover:text-accent-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">
            {videosWithSource.length ? "Watch" : "Details"}
            <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

function TeamSummary({ team, score, align }) {
  const initials = team?.name ? team.name.trim().split(/\s+/).slice(0, 2).map((word) => word[0]).join("").toUpperCase() : "?";
  return (
    <div className={`flex min-w-0 items-center gap-2 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-line bg-surface-800 sm:h-9 sm:w-9">
        {team?.logo ? <img src={team.logo} alt="" loading="lazy" className="h-full w-full object-contain p-1" /> : <span aria-hidden="true" className="text-[10px] font-bold text-gray-500">{initials}</span>}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-gray-300">{team?.name || "Team unavailable"}</span>
        <span className="block text-base font-bold tabular-nums text-white">{score ?? "—"}</span>
      </span>
    </div>
  );
}

function MatchDetailsDialog({ match, scores, players, videos, videoLoading, playbackErrors, setPlaybackErrors, onClose }) {
  const goals = Array.isArray(scores[match.matchesId]) ? scores[match.matchesId] : [];
  const homeScore = match.homeTeamScore ?? (Array.isArray(scores[match.matchesId]) ? goals.filter((score) => score.clubId === match.homeTeamId).length : null);
  const awayScore = match.awayTeamScore ?? (Array.isArray(scores[match.matchesId]) ? goals.filter((score) => score.clubId === match.awayTeamId).length : null);
  const videosWithSource = videos.filter((video) => video.videoUrl);
  const matchDate = new Date(match.date);

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/75 p-3 backdrop-blur-sm sm:p-5" role="presentation" onClick={onClose}>
      <section role="dialog" aria-modal="true" aria-labelledby="highlight-details-title" className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-xl border border-line-strong bg-surface-850 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-line bg-surface-900/95 p-4 backdrop-blur sm:p-5">
          <div className="min-w-0">
            <p className="eyebrow-accent">MATCH HIGHLIGHTS</p>
            <h2 id="highlight-details-title" className="mt-1 truncate text-lg font-bold text-white sm:text-xl">{match.leagueName}</h2>
          </div>
          <button type="button" aria-label="Close match highlights" onClick={onClose} className="shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-surface-700 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">
            <XMarkIcon aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 p-4 sm:p-6">
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 rounded-xl border border-line bg-surface-900 p-3 sm:gap-4 sm:p-5">
            <TeamSummary team={match.homeTeam} score={homeScore} align="left" />
            <span className="text-xs font-bold text-gray-500">FT</span>
            <TeamSummary team={match.awayTeam} score={awayScore} align="right" />
          </div>

          <section aria-label="Available match videos">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-white">Available videos</h3>
              {videosWithSource.length > 0 && <span className="text-xs text-gray-500">{videosWithSource.length} {videosWithSource.length === 1 ? "video" : "videos"}</span>}
            </div>
            {videoLoading ? (
              <div role="status" className="flex min-h-32 items-center justify-center gap-3 rounded-lg border border-line bg-surface-900 text-sm text-gray-400">
                <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-surface-600 border-t-accent" />
                Loading videos…
              </div>
            ) : videosWithSource.length ? (
              <div className="space-y-4">
                {videosWithSource.map((video, index) => (
                  <article key={video.id || `${match.matchesId}-${index}`} className="overflow-hidden rounded-xl border border-line bg-surface-900">
                    <div className="p-4">
                      <h4 className="font-semibold text-white">{video.title || "Match highlight"}</h4>
                      {video.description && <p className="mt-1 text-sm leading-6 text-gray-400">{video.description}</p>}
                    </div>
                    <div className="relative aspect-video bg-black">
                      {playbackErrors[video.id] ? (
                        <div role="alert" className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-5 text-center">
                          <VideoCameraIcon aria-hidden="true" className="h-8 w-8 text-gray-500" />
                          <p className="text-sm font-medium text-white">This video is currently unavailable.</p>
                          <p className="text-xs text-gray-400">Please try again later.</p>
                        </div>
                      ) : (
                        <video
                          className="h-full w-full"
                          controls
                          playsInline
                          preload="metadata"
                          poster={video.thumbnailUrl || undefined}
                          onError={() => setPlaybackErrors((previous) => ({ ...previous, [video.id]: true }))}
                        >
                          <source src={video.videoUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-line bg-surface-900 px-4 py-8 text-center">
                <VideoCameraIcon aria-hidden="true" className="mx-auto h-7 w-7 text-gray-500" />
                <p className="mt-3 text-sm text-gray-400">No video highlights are available for this match.</p>
              </div>
            )}
          </section>

          <section aria-label="Match details">
            <h3 className="mb-3 text-base font-semibold text-white">Match details</h3>
            <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1.5"><CalendarDaysIcon aria-hidden="true" className="h-4 w-4" />{Number.isNaN(matchDate.getTime()) ? "Date unavailable" : matchDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
              <span>{match.time}</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[match.homeTeamId, match.awayTeamId].map((clubId, index) => {
                const team = index === 0 ? match.homeTeam : match.awayTeam;
                const teamGoals = goals.filter((score) => score.clubId === clubId).sort((a, b) => a.timeScored - b.timeScored);
                return (
                  <div key={`${clubId}-${index}`} className="min-w-0 rounded-lg border border-line bg-surface-900 p-4">
                    <h4 className="mb-3 truncate text-sm font-semibold text-gray-200">{team?.name || (index === 0 ? "Home team" : "Away team")}</h4>
                    {teamGoals.length ? (
                      <ul className="space-y-2">
                        {teamGoals.map((score) => (
                          <li key={score.id} className="flex items-start gap-2 text-sm">
                            {score.timeScored != null && <span className="shrink-0 text-accent-soft">{score.timeScored}′</span>}
                            <span className="min-w-0 text-gray-300">{score.playerId ? players[score.playerId] || "Loading player…" : "Unattributed goal"}</span>
                            {(score.isPenalty || score.isOwnGoal) && <span className="shrink-0 text-gray-500">{score.isPenalty ? "(P)" : "(OG)"}</span>}
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-sm text-gray-500">No goals recorded.</p>}
                  </div>
                );
              })}
            </div>
          </section>
          <button type="button" onClick={onClose} className="btn-secondary min-h-11 w-full">Close details</button>
        </div>
      </section>
    </div>
  );
}

export default Highlights;
